import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H2Atom } from '@atoms/text-atoms/text-atoms';
import { motion, useScroll } from 'framer-motion';
import { SecondaryNavProps } from './secondary-nav.types';
import { TabLinkAtom } from '@atoms/link-atoms/link-atoms';

const SecondaryNav: React.FC<SecondaryNavProps> = ({
  title,
  tabs,
  colour = vars.coreDarkGrey,
  className,
}) => {
  const [primaryNavHidden, setPrimaryNavHidden] = React.useState(false);

  const { scrollY } = useScroll();

  const variants = {
    standardYPosition: { y: 0 },
    topYPosition: { y: -56 },
  };

  const handleHidingNav = () => {
    let scrollYValue = scrollY.get();
    let prevScrollYValue = scrollY.getPrevious();
    if (scrollYValue < prevScrollYValue) {
      return setPrimaryNavHidden(false);
    } else if (scrollYValue > 400 && scrollYValue > prevScrollYValue) {
      return setPrimaryNavHidden(true);
    }
  };

  React.useEffect(() => {
    scrollY.onChange(() => handleHidingNav());
  });

  return (
    <motion.div
      className={className}
      variants={variants}
      animate={primaryNavHidden ? 'topYPosition' : 'standardYPosition'}
      transition={{ ease: [0.1, 0.25, 0.3, 1], duration: 0.2 }}
      role="navigation"
      aria-label={`Secondary navigation for ${title || 'this section'}`}
    >
      <div className="container">
        {title && <H2Atom>{title}</H2Atom>}
        {Array.isArray(tabs) && (
          <div className="tab-container">
            {tabs.map((tab, index) => {
              if (tab.title && tab.url) {
                const dataGtmId = `${GTM_ID.SECONDARY_NAV_TAB}-${index}`;
                const dataTestId = `secondary-nav-tab-${index}`;

                return (
                  <TabLinkAtom
                    gtmId={dataGtmId}
                    dataTestId={dataTestId}
                    url={tab.url}
                    key={index}
                    colour={colour}
                    className={tab.active ? 'active' : ''}
                  >
                    {tab.title}
                  </TabLinkAtom>
                );
              } else {
                console.warn(
                  `Secondary nav with title ${title} has tab with missing key(s). Title: ${tab.title}, URL: ${tab.url}`
                );
                return null;
              }
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const StyledSecondaryNav = styled(SecondaryNav)`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: white;
  color: ${vars.coreDarkGrey};
  z-index: 250;
  border-bottom: 1px solid lightGrey;

  ${H2Atom} {
    ${vars.fontSize7}
    padding: 0;
    margin-top: ${vars.spacingL};
    margin-right: 0;
    line-height: 1.1;
    white-space: nowrap;
    position: relative;
    top: 0;
  }

  .container {
    transition: transform 0.3s ease;
    display: flex;
    flex-direction: column;
    padding: ${vars.spacingXs} ${vars.spacingM};
  }

  .tab-container {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    align-items: center;
    height: 40px;
    padding-right: ${vars.spacingXl};

    &::-webkit-scrollbar {
      display: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0px;
      right: 0;
      width: ${vars.spacingXl};
      background: linear-gradient(to right, rgba(255, 255, 255, 0.7), white);
    }
  }

  @media screen and (min-width: ${vars.breakPointTablet}) {
    position: sticky;
    height: 92px;
    top: ${vars.primaryNavHeight};
    margin: 0;

    .container {
      flex-direction: column;
      align-items: flex-start;
      margin: 0 auto;
      max-width: ${vars.mainMaxWidth};
      padding-left: ${vars.spacingL};
      padding-right: ${vars.spacingL};
      width: 100%;
    }

    ${H2Atom} {
      margin-right: ${vars.spacingL};
      margin-top: ${vars.spacingS};
    }

    .tab-container {
      height: 50px;
    }
  }

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    height: 54px;

    .container {
      flex-direction: row;
      align-items: center;
    }

    ${H2Atom} {
      margin-top: 0;
    }
  }
`;

export default StyledSecondaryNav;
